import { useState, useMemo } from "react";
import { Search, X } from "lucide-react";

// Curated icon library — emoji-based, displays as colored icons
const ICONS = {
  "Popular": ["🚀", "✨", "💡", "⭐", "🎯", "📌", "🔥", "💎", "🏆", "🎉", "✅", "🌟"],
  "Documentation": ["📁", "📂", "📄", "📃", "📋", "📑", "🗂️", "📚", "📖", "📕", "📗", "📘", "📙", "📓", "📔", "📒", "📰", "🗒️", "🗃️"],
  "Tech & Tools": ["💻", "🖥️", "⌨️", "🖱️", "🖨️", "📱", "📲", "☎️", "🔌", "🔋", "💾", "💿", "📀", "🛠️", "🔧", "🔨", "⚙️", "🔩", "🧰"],
  "Business": ["💼", "🏢", "🏦", "💳", "💰", "💵", "💴", "💶", "💷", "🧾", "📊", "📈", "📉", "💹", "🏷️", "🪙", "💸", "🤝"],
  "Communication": ["📧", "📨", "📩", "📮", "📤", "📥", "✉️", "💬", "💭", "🗨️", "🗯️", "📢", "📣", "📯", "🔔", "🔕", "📞", "📟", "📠"],
  "People & Account": ["👤", "👥", "👨‍💼", "👩‍💼", "🧑‍💻", "👨‍💻", "👩‍💻", "🧑‍🎓", "👮", "🕵️", "🙋", "🙋‍♀️", "🙋‍♂️", "🤵", "🧑", "👶", "🧒"],
  "Security & Locks": ["🔐", "🔒", "🔓", "🔑", "🗝️", "🛡️", "🚨", "⚠️", "🔰", "📛"],
  "Web & Cloud": ["🌐", "🌍", "🌎", "🌏", "☁️", "⛅", "🌤️", "🌥️", "🌦️", "🌧️", "🔗", "🛰️", "📡", "📶"],
  "Status & Signals": ["✅", "❌", "⭕", "❗", "❓", "❕", "❔", "‼️", "⁉️", "💢", "🟢", "🔴", "🟡", "🟠", "🔵", "🟣", "⚪", "⚫"],
  "Faces": ["😀", "😃", "😄", "😊", "🙂", "😉", "😍", "🤩", "😎", "🤔", "🤓", "😇", "🥳", "🙌", "👏", "👍", "👎", "👌", "🤝", "💪"],
  "Travel & Places": ["🏠", "🏡", "🏘️", "🏥", "🏨", "🏪", "🏫", "🏬", "🏭", "🏛️", "⛪", "🕌", "🗼", "🗽", "🏰", "🏯", "🌆", "🌃", "✈️", "🚀", "🛰️"],
  "Arrows & Misc": ["➡️", "⬅️", "⬆️", "⬇️", "↗️", "↘️", "↙️", "↖️", "↕️", "↔️", "🔄", "🔃", "🔁", "🔂", "▶️", "⏸️", "⏹️", "⏺️", "🎵", "🎶"],
};

const ALL_ICONS = Object.values(ICONS).flat();

export default function IconPicker({ value, onChange, testId = "icon-picker" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("Popular");

  // basic emoji "keyword" search using category names
  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    const matched = [];
    for (const [cat, icons] of Object.entries(ICONS)) {
      if (cat.toLowerCase().includes(q)) {
        matched.push(...icons);
      }
    }
    return matched.length ? Array.from(new Set(matched)) : ALL_ICONS;
  }, [query]);

  const icons = searchResults || ICONS[activeCat] || [];

  return (
    <div className="relative" data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)}
        data-testid={`${testId}-trigger`}
        className="w-20 h-12 border-2 border-zinc-200 hover:border-zinc-400 rounded-sm text-2xl flex items-center justify-center bg-white">
        {value || "📁"}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-2 left-0 z-50 w-[380px] bg-white border border-zinc-200 rounded-sm shadow-2xl"
            data-testid={`${testId}-panel`}>
            <div className="p-3 border-b border-zinc-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-700">Pick an icon</h4>
                <button type="button" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-900">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search categories (e.g. people, tech, business)"
                  data-testid={`${testId}-search`}
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-zinc-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#41B5E6]" />
              </div>
            </div>

            {!query && (
              <div className="flex gap-0.5 overflow-x-auto border-b border-zinc-200 px-2 py-1.5">
                {Object.keys(ICONS).map((cat) => (
                  <button key={cat} type="button" onClick={() => setActiveCat(cat)}
                    className={`whitespace-nowrap px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors ${
                      activeCat === cat ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"
                    }`}>
                    {cat}
                  </button>
                ))}
              </div>
            )}

            <div className="p-2 max-h-[260px] overflow-y-auto">
              <div className="grid grid-cols-9 gap-1">
                {icons.map((icon, i) => (
                  <button key={`${icon}-${i}`} type="button"
                    onClick={() => { onChange(icon); setOpen(false); }}
                    data-testid={`${testId}-icon-${i}`}
                    className={`w-9 h-9 rounded-sm text-xl flex items-center justify-center hover:bg-zinc-100 transition-colors ${
                      value === icon ? "bg-zinc-100 ring-2 ring-[#41B5E6]" : ""
                    }`}
                    title={icon}>
                    {icon}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
